import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { itemAPI, categoryAPI, uploadAPI, attributeAPI } from '../../services/api';
import { TableSkeleton, EmptyState } from '../../components/skeletons';
import { AttributeFields } from '../../components/AttributeFields';
import { renderIcon, isImageUrl } from './TaxonomyManager';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const CREAM = '#FAF9F6';

const formatOptionLabel = (icon, name) => {
  if (!icon) return name;
  if (isImageUrl(icon)) return name;
  return `${icon} ${name}`;
};

const MAX_MEDIA = 4; // max total images + videos per item

// ─── Media Uploader Component ─────────────────────────────────────────────────
function MediaUploader({ mediaItems, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const fileArr = Array.from(files);
    if (!fileArr.length) return;

    const remaining = MAX_MEDIA - mediaItems.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_MEDIA} media items allowed. Remove some to add more.`);
      return;
    }
    const toUpload = fileArr.slice(0, remaining);
    if (fileArr.length > remaining) {
      setError(`Only ${remaining} slot(s) left. First ${remaining} file(s) will be uploaded.`);
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm', 'video/quicktime'];
    const invalid = toUpload.filter(f => !allowed.includes(f.type));
    if (invalid.length) {
      setError(`Unsupported file type: ${invalid.map(f => f.name).join(', ')}`);
      return;
    }
    if (!invalid.length && fileArr.length > remaining) {} // already set error above
    setError(prev => invalid.length ? prev : (fileArr.length > remaining ? prev : ''));
    setUploading(true);
    setUploadProgress(0);

    try {
      const res = await uploadAPI.uploadItemMedia(toUpload, (evt) => {
        if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      const uploaded = res.data.data; // [{ url, type, originalName }]
      onChange([...mediaItems, ...uploaded]);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeMedia = (idx) => {
    onChange(mediaItems.filter((_, i) => i !== idx));
  };

  const isFull = mediaItems.length >= MAX_MEDIA;

  const images = mediaItems.filter(m => m.type === 'image');
  const videos = mediaItems.filter(m => m.type === 'video');

  return (
    <div>
      {/* Media counter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: MUTED }}>Images &amp; Videos</span>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
          background: isFull ? '#fef2f2' : '#f0fdf4',
          color: isFull ? '#dc2626' : GREEN,
        }}>
          {mediaItems.length}/{MAX_MEDIA} {isFull ? '— Full' : 'used'}
        </span>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (!isFull && !uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { if (!isFull) handleDrop(e); }}
        onClick={() => !uploading && !isFull && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${isFull ? '#fecaca' : dragOver ? GREEN : BORDER}`,
          borderRadius: 12,
          padding: '24px 16px',
          textAlign: 'center',
          cursor: isFull ? 'not-allowed' : uploading ? 'not-allowed' : 'pointer',
          background: isFull ? '#fef9f9' : dragOver ? 'rgba(27, 92, 58, 0.04)' : '#fafafa',
          transition: 'all 0.2s ease',
          marginBottom: 12,
          opacity: isFull ? 0.7 : 1,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <div>
            <div style={{ fontSize: 28, marginBottom: 8 }}>⬆️</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: GREEN, marginBottom: 8 }}>
              Uploading to AWS S3... {uploadProgress}%
            </div>
            <div style={{ width: '100%', height: 6, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 99, background: GREEN,
                width: `${uploadProgress}%`, transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        ) : isFull ? (
          <div>
            <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 2 }}>Maximum {MAX_MEDIA} media reached</div>
            <div style={{ fontSize: 11, color: MUTED }}>Remove an item below to upload more</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: CHAR, marginBottom: 4 }}>
              Drop images &amp; videos here, or click to browse
            </div>
            <div style={{ fontSize: 11, color: MUTED }}>
              JPG, PNG, WEBP, GIF, MP4, WEBM, MOV • Max {MAX_MEDIA} total • 200MB each
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#dc2626', marginBottom: 10 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Images preview */}
      {images.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Images ({images.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {images.map((m, i) => {
              const globalIdx = mediaItems.indexOf(m);
              return (
                <div key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                  <img
                    src={m.url}
                    alt={m.originalName}
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: `1px solid ${BORDER}` }}
                    onError={(e) => { e.target.src = 'https://placehold.co/80x80?text=📦'; }}
                  />
                  <button
                    type="button"
                    onClick={() => removeMedia(globalIdx)}
                    style={{
                      position: 'absolute', top: -6, right: -6,
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#dc2626', color: '#fff', border: 'none',
                      cursor: 'pointer', fontSize: 10, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Videos preview */}
      {videos.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Videos ({videos.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {videos.map((m, i) => {
              const globalIdx = mediaItems.indexOf(m);
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f9fafb', borderRadius: 8, padding: '8px 12px', border: `1px solid ${BORDER}` }}>
                  <span style={{ fontSize: 20 }}>🎬</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: CHAR, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.originalName}
                    </div>
                    <a href={m.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: GREEN, textDecoration: 'none' }}>
                      View on S3 ↗
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeMedia(globalIdx)}
                    style={{ background: 'none', border: `1px solid #fecaca`, borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ItemManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Filter states
  const [filterCat, setFilterCat] = useState('');
  const [filterSubCat, setFilterSubCat] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterSubcategories, setFilterSubcategories] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);

  // Taxonomy lists for selectors
  const [categories, setCategories] = useState([]);
  const [formSubcategories, setFormSubcategories] = useState([]);
  const [formBrands, setFormBrands] = useState([]);

  // Modal / Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [form, setForm] = useState({
    name: '',
    categoryId: '',
    subCategoryId: '',
    brandId: '',
    mrp: '',
    description: '',
    tags: '',
    isFeatured: false,
    displayOrder: 0,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    specs: [],
  });

  // Media state: array of { url, type, originalName }
  const [mediaItems, setMediaItems] = useState([]);
  // Category-driven spec schema for the item form (same one buyers fill).
  const [itemAttrSchema, setItemAttrSchema] = useState([]);

  // AI generation states
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiBase64, setAiBase64] = useState('');
  const [aiMimeType, setAiMimeType] = useState('image/jpeg');
  const [aiSize, setAiSize] = useState(0);
  const [aiError, setAiError] = useState('');
  const [aiSaving, setAiSaving] = useState(false);
  const [aiIndex, setAiIndex] = useState(0);

  // Search photo states
  const [searchPhotos, setSearchPhotos] = useState([]);
  const [selectedPhotoUrls, setSelectedPhotoUrls] = useState([]);

  const handleAiGenerate = async () => {
    if (!form.name?.trim()) {
      setAiError('Please enter a product name first.');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    setAiBase64('');
    setAiSize(0);
    setSearchPhotos([]);
    setSelectedPhotoUrls([]);
    try {
      const res = await uploadAPI.searchPhotos(form.name.trim());
      if (res.data?.success && Array.isArray(res.data?.results)) {
        setSearchPhotos(res.data.results);
      } else {
        setAiError(res.data?.message || 'Failed to search product photos.');
      }
    } catch (err) {
      console.error(err);
      setAiError(err.response?.data?.message || 'Error finding product photos.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSaveSelectedPhoto = async () => {
    if (selectedPhotoUrls.length === 0) return;
    setAiSaving(true);
    setAiError('');
    try {
      const savedMedia = [];
      for (const url of selectedPhotoUrls) {
        try {
          const res = await uploadAPI.downloadUrl(url, form.name.trim());
          if (res.data?.success && res.data?.data) {
            savedMedia.push(res.data.data);
          }
        } catch (err) {
          console.error(`Failed to download ${url}:`, err);
        }
      }

      if (savedMedia.length > 0) {
        setMediaItems(prev => [...prev, ...savedMedia]);
        setSearchPhotos([]);
        setSelectedPhotoUrls([]);
      } else {
        setAiError('Failed to save selected images. The source websites might be blocking downloads.');
      }
    } catch (err) {
      console.error(err);
      setAiError(err.response?.data?.message || 'Error saving selected images.');
    } finally {
      setAiSaving(false);
    }
  };

  const handleAiSave = async () => {
    if (!aiBase64) return;
    setAiSaving(true);
    setAiError('');
    try {
      const ext = aiMimeType.split('/')[1]?.split(';')[0] || 'jpg';
      const filename = `product-${form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}.${ext}`;
      const res = await uploadAPI.saveImageBase64(aiBase64, filename, aiMimeType);
      if (res.data?.success && res.data?.data) {
        setMediaItems(prev => [...prev, res.data.data]);
        setAiBase64('');
      } else {
        setAiError(res.data?.message || 'Failed to save image.');
      }
    } catch (err) {
      console.error(err);
      setAiError(err.response?.data?.message || 'Error saving image.');
    } finally {
      setAiSaving(false);
    }
  };

  const handleAiDiscard = () => {
    setAiBase64('');
    setAiError('');
  };

  const handleLocalImageGenerate = () => {
    if (!form.name?.trim()) {
      setAiError('Please enter a product name first.');
      return;
    }
    setAiError('');
    setAiBase64('');
    setAiSize(0);

    try {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 600;
      const ctx = canvas.getContext('2d');

      // Deterministic gradient selection based on hash of product name
      const name = form.name.trim();
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % 5;

      const presets = [
        { name: 'Forest Emerald', colors: ['#0f5132', '#082f1e', '#051b11'] },
        { name: 'Oceanic Sapphire', colors: ['#0a58ca', '#084298', '#031633'] },
        { name: 'Royal Amethyst', colors: ['#6f42c1', '#512da8', '#210f47'] },
        { name: 'Crimson Glow', colors: ['#dc3545', '#b02a37', '#491217'] },
        { name: 'Slate Onyx', colors: ['#495057', '#343a40', '#1c1f22'] }
      ];

      const preset = presets[index];

      // Draw background gradient
      const grad = ctx.createLinearGradient(0, 0, 600, 600);
      grad.addColorStop(0, preset.colors[0]);
      grad.addColorStop(0.5, preset.colors[1]);
      grad.addColorStop(1, preset.colors[2]);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 600, 600);

      // Radial light glow in center
      const glow = ctx.createRadialGradient(300, 300, 40, 300, 300, 240);
      glow.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
      glow.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(300, 300, 240, 0, Math.PI * 2);
      ctx.fill();

      // Draw framed border (poster template style)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 2;
      ctx.strokeRect(28, 28, 544, 544);

      // Draw a secondary outer accent line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
      ctx.lineWidth = 1;
      ctx.strokeRect(20, 20, 560, 560);

      // Text rendering & wrapping
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;

      // Set font size depending on title length
      let fontSize = 38;
      if (name.length > 30) fontSize = 30;
      if (name.length > 50) fontSize = 24;

      ctx.font = `bold ${fontSize}px "Inter", "Helvetica Neue", "Arial", sans-serif`;

      const words = name.split(' ');
      let line = '';
      const lines = [];
      const maxWidth = 460;
      const lineHeight = fontSize + 10;

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
          lines.push(line.trim());
          line = words[n] + ' ';
        } else {
          line = testLine;
        }
      }
      lines.push(line.trim());

      const totalHeight = lines.length * lineHeight;
      let startY = 300 - (totalHeight / 2) + (lineHeight / 2);

      // Render text
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], 300, startY);
        startY += lineHeight;
      }

      // Reset shadow for drawing lines
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Elegant accent line below text block
      const endY = 300 + (totalHeight / 2) + 20;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(250, endY);
      ctx.lineTo(350, endY);
      ctx.stroke();

      // Convert to base64 data
      const dataUrl = canvas.toDataURL('image/png');
      const base64Clean = dataUrl.replace(/^data:image\/png;base64,/, '');

      setAiBase64(base64Clean);
      setAiMimeType('image/png');
      setAiSize(Math.round((base64Clean.length * 3) / 4));
    } catch (err) {
      console.error('Local canvas generation error:', err);
      setAiError('Failed to generate product poster card.');
    }
  };

  const loadItems = useCallback(() => {
    setLoading(true);
    itemAPI.list({
      search,
      categoryId: filterCat || undefined,
      subCategoryId: filterSubCat || undefined,
      brandId: filterBrand || undefined,
      page,
      limit,
      featured: undefined,
    })
      .then(res => {
        setItems(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(err => console.error('Failed to load items:', err))
      .finally(() => setLoading(false));
  }, [search, filterCat, filterSubCat, filterBrand, page, limit]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    categoryAPI.flat({ admin: true })
      .then(res => setCategories(res.data.data?.categories || []))
      .catch(err => console.error('Failed to load categories:', err));
  }, []);

  useEffect(() => {
    setAiIndex(0);
    setAiBase64('');
    setAiError('');
    setSearchPhotos([]);
    setSelectedPhotoUrls([]);
  }, [form.name]);

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };

  const handleCategoryChange = async (catId) => {
    setForm(prev => ({ ...prev, categoryId: catId, subCategoryId: '', brandId: '' }));
    setFormSubcategories([]); setFormBrands([]);
    if (catId) {
      try { const res = await categoryAPI.children(catId, { admin: true }); setFormSubcategories(res.data.data || []); }
      catch (err) { console.error(err); }
    }
  };

  const handleSubcategoryChange = async (subId) => {
    setForm(prev => ({ ...prev, subCategoryId: subId, brandId: '' }));
    setFormBrands([]);
    if (subId) {
      try { const res = await categoryAPI.children(subId, { admin: true }); setFormBrands(res.data.data || []); }
      catch (err) { console.error(err); }
    }
  };

  const handleFilterCategoryChange = async (catId) => {
    setFilterCat(catId); setFilterSubCat(''); setFilterBrand('');
    setFilterSubcategories([]); setFilterBrands([]); setPage(1);
    if (catId) {
      try { const res = await categoryAPI.children(catId, { admin: true }); setFilterSubcategories(res.data.data || []); }
      catch (err) { console.error(err); }
    }
  };

  const handleFilterSubcategoryChange = async (subId) => {
    setFilterSubCat(subId); setFilterBrand(''); setFilterBrands([]); setPage(1);
    if (subId) {
      try { const res = await categoryAPI.children(subId, { admin: true }); setFilterBrands(res.data.data || []); }
      catch (err) { console.error(err); }
    }
  };

  const handleFilterBrandChange = (brandId) => { setFilterBrand(brandId); setPage(1); };

  const blankForm = () => ({
    name: '', categoryId: '', subCategoryId: '', brandId: '',
    mrp: '', description: '', tags: '', isFeatured: false,
    displayOrder: 0, seoTitle: '', seoDescription: '', seoKeywords: '', specs: [],
  });

  const openAddDrawer = () => {
    setEditingItem(null);
    setFormSubcategories([]); setFormBrands([]);
    setForm(blankForm());
    setMediaItems([]);
    setAiBase64('');
    setAiMimeType('image/jpeg');
    setAiSize(0);
    setAiError('');
    setAiGenerating(false);
    setAiSaving(false);
    setAiIndex(0);
    setSearchPhotos([]);
    setSelectedPhotoUrls([]);
    setDrawerOpen(true);
  };

  const openEditDrawer = async (item) => {
    setEditingItem(item);
    setSaving(true);
    setAiBase64('');
    setAiMimeType('image/jpeg');
    setAiSize(0);
    setAiError('');
    setAiGenerating(false);
    setAiSaving(false);
    setAiIndex(0);
    setSearchPhotos([]);
    setSelectedPhotoUrls([]);
    try {
      const subRes = await categoryAPI.children(item.categoryId?._id || item.categoryId, { admin: true });
      setFormSubcategories(subRes.data.data || []);
      if (item.subCategoryId) {
        const brandRes = await categoryAPI.children(item.subCategoryId?._id || item.subCategoryId, { admin: true });
        setFormBrands(brandRes.data.data || []);
      } else { setFormBrands([]); }

      // Build specsArr
      const specsArr = [];
      if (item.specs && typeof item.specs === 'object') {
        Object.entries(item.specs).forEach(([k, v]) => specsArr.push({ key: k, value: String(v) }));
      }

      // Reconstruct mediaItems from existing images[] array + videos
      // Backward compat: if images[] is empty but image exists, use it as first
      const existingMedia = [];
      const allImages = Array.isArray(item.images) && item.images.length > 0
        ? item.images
        : (item.image ? [item.image] : []);
      allImages.forEach((url, i) => existingMedia.push({ url, type: 'image', originalName: `image-${i + 1}` }));
      if (Array.isArray(item.videos)) {
        item.videos.forEach((v, i) => existingMedia.push({ url: v, type: 'video', originalName: `video-${i + 1}` }));
      }
      setMediaItems(existingMedia);

      setForm({
        name: item.name || '',
        categoryId: item.categoryId?._id || item.categoryId || '',
        subCategoryId: item.subCategoryId?._id || item.subCategoryId || '',
        brandId: item.brandId?._id || item.brandId || '',
        mrp: item.mrp || '',
        description: item.description || '',
        tags: Array.isArray(item.tags) ? item.tags.join(', ') : item.tags || '',
        isFeatured: !!item.isFeatured,
        displayOrder: item.displayOrder || 0,
        seoTitle: item.seoTitle || '',
        seoDescription: item.seoDescription || '',
        seoKeywords: Array.isArray(item.seoKeywords) ? item.seoKeywords.join(', ') : item.seoKeywords || '',
        specs: specsArr,
      });
      setDrawerOpen(true);
    } catch (err) {
      alert('Failed to pre-fill item data');
      console.error(err);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try { await itemAPI.remove(id); loadItems(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete item.'); }
  };

  // ── Category-driven spec fields (same schema buyers fill when bidding) ────
  const normKey = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '');

  // schema for the form's current category/sub-category
  useEffect(() => {
    if (!form.categoryId) { setItemAttrSchema([]); return undefined; }
    let cancelled = false;
    attributeAPI.resolve({ categoryId: form.categoryId, subCategoryId: form.subCategoryId || undefined })
      .then(r => { if (!cancelled) setItemAttrSchema(r.data?.data?.attributes || []); })
      .catch(() => { if (!cancelled) setItemAttrSchema([]); });
    return () => { cancelled = true; };
  }, [form.categoryId, form.subCategoryId]);

  // existing key/value specs -> schema values
  const specValues = useMemo(() => {
    const byNorm = {};
    (form.specs || []).forEach(s => { if (s.key) byNorm[normKey(s.key)] = s.value; });
    const out = {};
    itemAttrSchema.forEach(a => {
      const hit = byNorm[normKey(a.key)] ?? byNorm[normKey(a.label)];
      if (hit === undefined || hit === '') return;
      if (a.type === 'multiselect') {
        const keep = String(hit).split(',').map(x => x.trim()).filter(x => a.options.includes(x));
        if (keep.length) out[a.key] = keep;
      } else if (a.type === 'number') {
        const n = parseFloat(String(hit).replace(/[^\d.]/g, ''));
        if (isFinite(n)) out[a.key] = n;
      } else if (a.type === 'boolean') {
        out[a.key] = ['true', 'yes', '1'].includes(String(hit).toLowerCase());
      } else out[a.key] = String(hit);
    });
    return out;
  }, [form.specs, itemAttrSchema]);

  // writing a schema field back into the key/value spec list (label as the key)
  const setSchemaSpec = (key, value) => {
    const def = itemAttrSchema.find(a => a.key === key);
    const label = def?.label || key;
    const str = Array.isArray(value) ? value.join(', ')
      : typeof value === 'boolean' ? (value ? 'Yes' : 'No')
      : String(value ?? '');
    setForm(prev => {
      const specs = [...(prev.specs || [])];
      const idx = specs.findIndex(s => normKey(s.key) === normKey(key) || normKey(s.key) === normKey(label));
      if (str === '') { if (idx >= 0) specs.splice(idx, 1); }
      else if (idx >= 0) specs[idx] = { ...specs[idx], key: specs[idx].key || label, value: str };
      else specs.push({ key: label, value: str });
      return { ...prev, specs };
    });
  };

  // Specs helpers
  const addSpecField = () => setForm(prev => ({ ...prev, specs: [...prev.specs, { key: '', value: '' }] }));
  const removeSpecField = (idx) => setForm(prev => ({ ...prev, specs: prev.specs.filter((_, i) => i !== idx) }));
  const handleSpecChange = (idx, field, value) => {
    const nextSpecs = [...form.specs];
    nextSpecs[idx][field] = value;
    setForm(prev => ({ ...prev, specs: nextSpecs }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert('Name is required.');
    if (!form.categoryId) return alert('Category is required.');
    if (!form.subCategoryId) return alert('Subcategory is required.');

    setSaving(true);

    const specsObj = {};
    form.specs.forEach(s => { if (s.key.trim()) specsObj[s.key.trim()] = s.value.trim(); });

    // Separate images vs videos from mediaItems
    const imageItems = mediaItems.filter(m => m.type === 'image');
    const videoItems = mediaItems.filter(m => m.type === 'video');
    const primaryImage = imageItems.length > 0 ? imageItems[0].url : '';
    const allImageUrls = imageItems.map(img => img.url);
    const videoUrls = videoItems.map(v => v.url);

    const body = {
      name: form.name.trim(),
      categoryId: form.categoryId,
      subCategoryId: form.subCategoryId,
      brandId: form.brandId || null,
      mrp: form.mrp ? parseFloat(form.mrp) : 0,
      description: form.description.trim(),
      image: primaryImage,
      images: allImageUrls,
      videos: videoUrls,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      isFeatured: form.isFeatured,
      displayOrder: parseInt(form.displayOrder) || 0,
      seoTitle: form.seoTitle.trim(),
      seoDescription: form.seoDescription.trim(),
      seoKeywords: form.seoKeywords ? form.seoKeywords.split(',').map(t => t.trim()).filter(Boolean) : [],
      specs: specsObj,
    };

    try {
      if (editingItem) { await itemAPI.update(editingItem._id, body); }
      else { await itemAPI.create(body); }
      setDrawerOpen(false);
      loadItems();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save item.');
    } finally { setSaving(false); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ background: CREAM, minHeight: '80vh', position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR }}>
            Manage Items
          </h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>
            Create and maintain your product catalog items, categories, brands and spec sheets.
          </p>
        </div>
        <button
          onClick={openAddDrawer}
          style={{
            background: GREEN, color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 2px 4px rgba(27, 92, 58, 0.15)'
          }}
        >
          <span>➕</span> Add Item
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 250px', position: 'relative' }}>
          <input
            className="form-input"
            placeholder="Search items by name, tags or description..."
            value={search}
            onChange={handleSearchChange}
            style={{ width: '100%', paddingLeft: 36, fontSize: 13 }}
          />
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: 14 }}>🔍</span>
        </div>

        <select className="form-input" value={filterCat} onChange={(e) => handleFilterCategoryChange(e.target.value)} style={{ flex: '1 1 140px', fontSize: 13, background: '#fff' }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>

        <select className="form-input" value={filterSubCat} onChange={(e) => handleFilterSubcategoryChange(e.target.value)} disabled={!filterCat} style={{ flex: '1 1 140px', fontSize: 13, background: '#fff' }}>
          <option value="">All Subcategories</option>
          {filterSubcategories.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>

        <select className="form-input" value={filterBrand} onChange={(e) => handleFilterBrandChange(e.target.value)} disabled={!filterSubCat} style={{ flex: '1 1 140px', fontSize: 13, background: '#fff' }}>
          <option value="">All Brands</option>
          {filterBrands.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
        </select>

        {(search || filterCat || filterSubCat || filterBrand) && (
          <button
            onClick={() => { setSearch(''); setFilterCat(''); setFilterSubCat(''); setFilterBrand(''); setFilterSubcategories([]); setFilterBrands([]); setPage(1); }}
            style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '8px 14px', fontSize: 12, color: CHAR, cursor: 'pointer', fontWeight: 600 }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Items table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: `2px solid ${BORDER}` }}>
              {['Item Name', 'Category', 'Subcategory', 'Brand', 'Media', 'Featured', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                  <td style={{ padding: '12px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center' }}>
                <EmptyState title="No catalog items found" description="Click Add Item above to publish products to the catalog." />
              </td></tr>
            ) : (
              items.map(item => (
                <tr key={item._id} style={{ borderBottom: `1px solid ${BORDER}`, background: '#fff' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {item.image ? (
                        <img src={item.image} alt={item.name}
                          style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', background: '#f3f4f6' }}
                          onError={(e) => { e.target.src = 'https://placehold.co/100x100?text=📦'; }}
                        />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📦</div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, color: CHAR, fontSize: 13 }}>{item.name}</div>
                        {item.mrp > 0 && <div style={{ fontSize: 11, color: MUTED }}>MRP: ₹{item.mrp.toLocaleString('en-IN')}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: CHAR }}>
                    {renderIcon(item.categoryId?.icon)}
                    {item.categoryId?.name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: CHAR }}>
                    {renderIcon(item.subCategoryId?.icon)}
                    {item.subCategoryId?.name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: CHAR }}>
                    {renderIcon(item.brandId?.icon)}
                    {item.brandId?.name || <span style={{ color: MUTED, fontStyle: 'italic', fontSize: 12 }}>None</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {item.image && <span title="Has image" style={{ fontSize: 14 }}>🖼️</span>}
                      {item.videos?.length > 0 && (
                        <span title={`${item.videos.length} video(s)`} style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>
                          🎬 {item.videos.length}
                        </span>
                      )}
                      {!item.image && !item.videos?.length && <span style={{ color: MUTED, fontSize: 12 }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {item.isFeatured ? (
                      <span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>⭐ Yes</span>
                    ) : (
                      <span style={{ color: MUTED, fontSize: 12 }}>No</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEditDrawer(item)}
                        style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: GREEN, cursor: 'pointer', fontWeight: 600 }}>
                        ✏️ Edit
                      </button>
                      <button onClick={() => handleDelete(item._id, item.name)}
                        style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb' }}>
            <div style={{ fontSize: 12, color: MUTED }}>Showing {items.length} of {total} items</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>
                Previous
              </button>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 12, color: CHAR, fontWeight: 600 }}>
                Page {page} of {totalPages}
              </div>
              <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer Overlay */}
      {drawerOpen && (
        <>
          <div onClick={() => setDrawerOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)', zIndex: 1000 }}
          />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 600, maxWidth: '100%',
            background: '#fff', boxShadow: '-10px 0 30px rgba(0,0,0,0.15)', zIndex: 1001,
            display: 'flex', flexDirection: 'column', animation: 'slideIn 0.3s ease-out'
          }}>
            {/* Drawer Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: CHAR, margin: 0 }}>
                  {editingItem ? 'Edit Item Details' : 'Add New Item'}
                </h3>
                <p style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>
                  {editingItem ? `Editing: ${editingItem.name}` : 'Fill in catalog fields to generate listing'}
                </p>
              </div>
              <button onClick={() => setDrawerOpen(false)}
                style={{ background: '#f3f4f6', border: 'none', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: CHAR }}>
                ✕
              </button>
            </div>

            {/* Drawer Form */}
            <form onSubmit={handleSave} style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

              {/* Section 1: Basic Info */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
                  1. Basic Details
                </h4>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Item Name *</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. iPhone 15 Pro Max" required style={{ width: '100%', fontSize: 13 }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Category *</label>
                    <select className="form-input" value={form.categoryId} onChange={(e) => handleCategoryChange(e.target.value)} required style={{ width: '100%', fontSize: 13, background: '#fff' }}>
                      <option value="">-- Select Category --</option>
                      {categories.map(c => <option key={c._id} value={c._id}>{formatOptionLabel(c.icon, c.name)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Subcategory *</label>
                    <select className="form-input" value={form.subCategoryId} onChange={(e) => handleSubcategoryChange(e.target.value)} required disabled={!form.categoryId} style={{ width: '100%', fontSize: 13, background: '#fff' }}>
                      <option value="">-- Select Subcategory --</option>
                      {formSubcategories.map(s => <option key={s._id} value={s._id}>{formatOptionLabel(s.icon, s.name)}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Brand <span style={{ color: MUTED, fontWeight: 400 }}>(Optional)</span></label>
                    <select className="form-input" value={form.brandId} onChange={(e) => setForm({ ...form, brandId: e.target.value })} disabled={!form.subCategoryId} style={{ width: '100%', fontSize: 13, background: '#fff' }}>
                      <option value="">-- Select Brand --</option>
                      {formBrands.map(b => <option key={b._id} value={b._id}>{formatOptionLabel(b.icon, b.name)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Market Price / MRP (₹)</label>
                    <input className="form-input" type="number" min="0" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} placeholder="e.g. 139999" style={{ width: '100%', fontSize: 13 }} />
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Description</label>
                  <textarea className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Enter detailed specifications or product description..." rows="3"
                    style={{ width: '100%', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Search Tags <span style={{ color: MUTED, fontWeight: 400 }}>(Comma separated)</span></label>
                    <input className="form-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. apple, iphone, phone, mobile" style={{ width: '100%', fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>Display Order</label>
                    <input className="form-input" type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} style={{ width: '100%', fontSize: 13 }} />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginTop: 16 }}>
                  <input type="checkbox" id="isFeatured" checked={form.isFeatured} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer', marginRight: 8 }} />
                  <label htmlFor="isFeatured" style={{ fontSize: 13, fontWeight: 600, color: CHAR, cursor: 'pointer' }}>
                    Feature this item on homepage / recommendations
                  </label>
                </div>
              </div>

              {/* Section 2: Media Upload */}
              <div style={{ marginBottom: 24, borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ fontSize: 12, fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 4px 0' }}>
                    2. Images & Videos
                  </h4>
                  <p style={{ fontSize: 11, color: MUTED, margin: 0 }}>
                    Upload product photos and demo videos. Stored on AWS S3. First image becomes the primary display image.
                  </p>
                </div>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                  <button
                    type="button"
                    disabled={aiGenerating || aiSaving || !form.name?.trim()}
                    onClick={handleAiGenerate}
                    style={{
                      background: 'linear-gradient(135deg, #1B5C3A 0%, #3a8c5d 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: !form.name?.trim() ? 'not-allowed' : 'pointer',
                      opacity: (aiGenerating || aiSaving || !form.name?.trim()) ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      boxShadow: '0 2px 4px rgba(27, 92, 58, 0.15)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>{aiGenerating ? '🔍 Searching...' : '✨ Find Product Photo'}</span>
                  </button>

                  <button
                    type="button"
                    disabled={aiGenerating || aiSaving || !form.name?.trim()}
                    onClick={handleLocalImageGenerate}
                    style={{
                      background: 'linear-gradient(135deg, #2b2d42 0%, #8d99ae 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: !form.name?.trim() ? 'not-allowed' : 'pointer',
                      opacity: (aiGenerating || aiSaving || !form.name?.trim()) ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      boxShadow: '0 2px 4px rgba(43, 45, 66, 0.15)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>🎨 Generate Image</span>
                  </button>

                  {!form.name?.trim() && (
                    <span style={{ fontSize: 11, color: MUTED }}>
                      ⚠️ Enter item name first to create/search photo
                    </span>
                  )}
                </div>

                {aiError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#dc2626', marginBottom: 16 }}>
                    ⚠️ {aiError}
                  </div>
                )}

                {searchPhotos && searchPhotos.length > 0 && (
                  <div style={{
                    background: '#f9fafb',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                      <span>Select Product Photos (Up to 3 Options)</span>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 8,
                      marginBottom: 16
                    }}>
                      {searchPhotos.map((photo, idx) => {
                        const isSelected = selectedPhotoUrls.includes(photo.image);
                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              setSelectedPhotoUrls(prev => {
                                if (prev.includes(photo.image)) {
                                  return prev.filter(url => url !== photo.image);
                                } else {
                                  return [...prev, photo.image];
                                }
                              });
                            }}
                            style={{
                              position: 'relative',
                              border: `2px solid ${isSelected ? GREEN : BORDER}`,
                              borderRadius: 6,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              background: '#fff',
                              boxShadow: isSelected ? '0 0 0 2px rgba(27, 92, 58, 0.2)' : 'none',
                              transition: 'all 0.2s ease',
                              aspectRatio: '1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <img
                              src={photo.thumbnail || photo.image}
                              alt={photo.title || 'Product option'}
                              referrerPolicy="no-referrer"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.src = 'https://placehold.co/150x150?text=Image+Unavailable';
                              }}
                            />
                            {isSelected && (
                              <div style={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                background: GREEN,
                                color: '#fff',
                                borderRadius: '50%',
                                width: 16,
                                height: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                fontWeight: 700
                              }}>
                                ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                      <button
                        type="button"
                        disabled={aiSaving || selectedPhotoUrls.length === 0}
                        onClick={handleSaveSelectedPhoto}
                        style={{
                          background: selectedPhotoUrls.length > 0 ? GREEN : BORDER,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '8px 16px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: selectedPhotoUrls.length > 0 ? 'pointer' : 'not-allowed',
                          opacity: aiSaving ? 0.7 : 1,
                        }}
                      >
                        {aiSaving ? 'Saving...' : `✨ Use & Upload Selected (${selectedPhotoUrls.length})`}
                      </button>
                      <button
                        type="button"
                        disabled={aiSaving}
                        onClick={() => {
                          setSearchPhotos([]);
                          setSelectedPhotoUrls([]);
                        }}
                        style={{
                          background: '#f3f4f6',
                          color: CHAR,
                          border: 'none',
                          borderRadius: 6,
                          padding: '8px 16px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ✕ Discard
                      </button>
                    </div>
                  </div>
                )}

                {aiBase64 && (
                  <div style={{
                    background: '#f9fafb',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 16,
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Product Photo Preview</span>
                      <span style={{ textTransform: 'none', fontWeight: 600, color: GREEN }}>
                        Size: {aiSize > 1024 * 1024 ? `${(aiSize / (1024 * 1024)).toFixed(2)} MB` : `${(aiSize / 1024).toFixed(1)} KB`}
                      </span>
                    </div>
                    <img
                      src={`data:${aiMimeType || 'image/jpeg'};base64,${aiBase64}`}
                      alt="AI Generated Product"
                      style={{
                        width: '100%',
                        maxWidth: 200,
                        height: 200,
                        objectFit: 'cover',
                        borderRadius: 10,
                        border: `1px solid ${BORDER}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        marginBottom: 12,
                        display: 'block',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                      <button
                        type="button"
                        disabled={aiSaving}
                        onClick={handleAiSave}
                        style={{
                          background: GREEN,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          opacity: aiSaving ? 0.7 : 1,
                        }}
                      >
                        {aiSaving ? 'Saving...' : '✨ Use & Upload'}
                      </button>
                      <button
                        type="button"
                        disabled={aiSaving}
                        onClick={handleAiDiscard}
                        style={{
                          background: '#f3f4f6',
                          color: CHAR,
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ✕ Discard
                      </button>
                    </div>
                  </div>
                )}

                <MediaUploader mediaItems={mediaItems} onChange={setMediaItems} />
              </div>

              {/* Section 3: Spec Sheet */}
              <div style={{ marginBottom: 24, borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h4 style={{ fontSize: 12, fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>
                    3. Spec Sheet (Key-Value Builder)
                  </h4>
                  <button type="button" onClick={addSpecField}
                    style={{ background: 'none', border: `1px solid ${GREEN}`, borderRadius: 6, color: GREEN, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    ➕ Add Field
                  </button>
                </div>
                {itemAttrSchema.length > 0 && (
                  <div style={{ marginBottom: 14, padding: 12, background: 'rgba(27,92,58,0.04)', borderRadius: 8, border: `1px solid ${GREEN}22` }}>
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: GREEN, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>Category specifications</div>
                    <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 10 }}>
                      Same fields buyers pick when bidding — filling these pre-fills their form.
                    </div>
                    <AttributeFields attributes={itemAttrSchema} values={specValues} onChange={setSchemaSpec} />
                  </div>
                )}
                {form.specs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px', background: '#f9fafb', borderRadius: 8, fontSize: 12, color: MUTED }}>
                    No spec fields configured. Set key-values like (RAM: 8GB, Storage: 256GB).
                  </div>
                ) : (
                  form.specs.map((spec, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input className="form-input" placeholder="Spec Key (e.g. RAM)" value={spec.key}
                        onChange={(e) => handleSpecChange(idx, 'key', e.target.value)} style={{ flex: 1, fontSize: 12, padding: '5px 8px' }} />
                      <input className="form-input" placeholder="Spec Value (e.g. 16GB)" value={spec.value}
                        onChange={(e) => handleSpecChange(idx, 'value', e.target.value)} style={{ flex: 1, fontSize: 12, padding: '5px 8px' }} />
                      <button type="button" onClick={() => removeSpecField(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#dc2626' }}>✕</button>
                    </div>
                  ))
                )}
              </div>

              {/* Section 4: SEO */}
              <div style={{ marginBottom: 20, borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>
                <h4 style={{ fontSize: 12, fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
                  4. SEO &amp; AEO Configuration
                </h4>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>SEO Meta Title</label>
                  <input className="form-input" value={form.seoTitle} onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
                    placeholder="Leave blank to auto-generate from name" style={{ width: '100%', fontSize: 13 }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>SEO Meta Description</label>
                  <textarea className="form-input" value={form.seoDescription} onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
                    placeholder="Leave blank to auto-generate search snippet" rows="2"
                    style={{ width: '100%', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 6 }}>SEO Keywords <span style={{ color: MUTED, fontWeight: 400 }}>(Comma separated)</span></label>
                  <input className="form-input" value={form.seoKeywords} onChange={(e) => setForm({ ...form, seoKeywords: e.target.value })}
                    placeholder="e.g. mobile, electronics, online store" style={{ width: '100%', fontSize: 13 }} />
                </div>
              </div>

              {/* Footer Actions */}
              <div style={{ display: 'flex', gap: 12, borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>
                <button type="submit" disabled={saving}
                  style={{ flex: 1, background: GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 4px rgba(27, 92, 58, 0.15)', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : editingItem ? 'Save Updates' : 'Add Product'}
                </button>
                <button type="button" onClick={() => setDrawerOpen(false)}
                  style={{ background: '#f3f4f6', color: CHAR, border: 'none', borderRadius: 8, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .form-input { display: block; padding: 8px 12px; border: 1px solid ${BORDER}; border-radius: 8px; color: ${CHAR}; outline: none; transition: border-color 0.2s; }
        .form-input:focus { border-color: ${GREEN}; }
        .card { background: #fff; border: 1px solid ${BORDER}; border-radius: 14px; }
        .spinner { width: 24px; height: 24px; border: 3px solid rgba(27, 92, 58, 0.1); border-top-color: ${GREEN}; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
